// A caster as a pawn whose head is its sign. `casterPawnShapes.ts` has the
// shapes. The caster select and the plates draw it as a symbol in the colors
// of the caster. The board draws the same shapes over a pawn in the color of
// its side.

import type { CasterId } from '../content/casters';
import { svg } from '../lib/svg';
import { casterPawnMotif, casterPawnOutline } from './casterPawnShapes';

/** Each drawing on the page needs its own ids for its mask and its clip. */
let nextDrawingNumber = 0;

const outlineClip = (id: string, outline: string): SVGClipPathElement =>
  svg('clipPath', { id }, [svg('path', { d: outline })]);

/**
 * The pawn as a symbol: the primary color with a hole for each cut, and the
 * second parts in the secondary color. `box` is the side of the box the
 * outline is drawn in.
 */
export const casterPawnMarkParts = (id: CasterId, box: number): SVGElement[] => {
  const { cuts, second } = casterPawnMotif(id);
  const outline = casterPawnOutline(id);
  const number = nextDrawingNumber++;
  const maskId = `caster-pawn-cut-${number}`;
  const clipId = `caster-pawn-clip-${number}`;
  return [
    svg('mask', { id: maskId, maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: box, height: box }, [
      svg('path', { d: outline, fill: '#fff' }),
      // Two shapes in one path make a ring, so the inner shape is not cut.
      ...cuts.map((d) => svg('path', { d, fill: '#000', 'fill-rule': 'evenodd' })),
    ]),
    outlineClip(clipId, outline),
    svg('path', { class: 'caster-mark__tile', d: outline, mask: `url(#${maskId})` }),
    svg(
      'g',
      { 'clip-path': `url(#${clipId})` },
      second.map((d) => svg('path', { class: 'caster-mark__second', d })),
    ),
  ];
};

/**
 * What a pawn on the board wears over its body: the cuts in the primary color
 * of the caster and the second parts in its secondary color, each with an ink
 * edge, so the body keeps the color of its side. `data-caster` gives the group
 * the colors. `board.css` has the colors.
 */
export const casterPawnWear = (id: CasterId): SVGElement[] => {
  const { cuts, second } = casterPawnMotif(id);
  const clipId = `caster-pawn-clip-${nextDrawingNumber++}`;
  return [
    outlineClip(clipId, casterPawnOutline(id)),
    svg('g', { class: 'glyph__wear', 'clip-path': `url(#${clipId})`, 'data-caster': id }, [
      ...cuts.map((d) => svg('path', { class: 'glyph__wear-cut', d, 'fill-rule': 'evenodd' })),
      ...second.map((d) => svg('path', { class: 'glyph__wear-part', d })),
    ]),
  ];
};
